import { ReactNode } from "react";
import { Link } from "react-router-dom";

import { Avatar } from "../../components/Avatar/Avatar";
import { Badge } from "../../components/Badge/Badge";
import { Menu } from "../../components/Menu/Menu";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import {
  ageCell,
  blockedLabel,
  carries,
  cellValue,
  columnID,
  isNumberType,
  PAGE_SIZE,
  plural,
  rowCard,
  type CardKey,
} from "./roster";
import type { RosterRow } from "./types";
import styles from "./SourceTab.module.css";

/**
 * The bot_roster table — one row per future bot.
 *
 * Two things about it are decisions rather than styling, and both come out of
 * the same fact: a project declares its own card, and eight declared keys are
 * eight columns on top of the four every project has.
 *
 *   - The NAME column is stuck to the left edge and everything else scrolls
 *     under it. A row read three columns to the right still has to say whose
 *     row it is.
 *   - Compact folds the whole card into one cell (`level 31 · trophies 1 240 ·
 *     badge veteran`), which is what makes a wide schema readable end to end.
 *     A key with no value is ABSENT from that cell, never printed as zero —
 *     the same rule a rolled card follows, and the difference between «this
 *     bot has no badge» and «this bot has the badge called empty string».
 *
 * ACCOUNT AGE survives Compact, where every card column goes. It is not part
 * of the card and never was: it is a property of the row itself, beside the
 * country and the behaviour, and the column picker — which holds card keys and
 * nothing else — does not offer it either. AVATAR is the same kind of column
 * for the same reason, and stands where the CSV puts it: second, right after
 * the name.
 *
 * An avatar outside `profile.allowed_avatars` is drawn in the warn colour with
 * a `title` that says what will happen to it. The colour alone says only «this
 * one is different»; the sentence says the bot this row becomes gets no avatar
 * and the row is spent all the same.
 *
 * Mockups: t-103/bots-source.html §2 and §3, t-117/bots-account-age.html §2
 * and §3.
 */

interface Props {
  rows: RosterRow[];
  /** Rows in the whole table, not on this page — what the footer counts. */
  total: number;
  loading: boolean;
  /** Fold the card into one cell instead of one column per key. */
  compact: boolean;
  /** Every key the project declares. */
  cardKeys: CardKey[];
  /**
   * `profile.allowed_avatars`. Empty means the project declared no whitelist —
   * there is then nothing to be outside of, and no cell is marked.
   */
  allowedAvatars: string[];
  /** The column ids drawn in Columns mode — see columnID. */
  visible: string[];
  /** How many rows of the table the page above skipped — what turns «showing
   *  1–50» into the truth about which fifty. */
  offset: number;
  /** Move to the page starting at that offset. */
  onPage: (offset: number) => void;
  projectID: string;
  onEdit: (row: RosterRow) => void;
  onDuplicate: (row: RosterRow) => void;
  onDelete: (row: RosterRow) => void;
  /** Drawn in place of the table when there is nothing to draw. */
  empty?: ReactNode;
}

export function RosterTable({
  rows,
  total,
  loading,
  compact,
  cardKeys,
  allowedAvatars,
  visible,
  offset,
  onPage,
  projectID,
  onEdit,
  onDuplicate,
  onDelete,
  empty,
}: Props) {
  const shown = compact
    ? []
    : cardKeys.filter((k) => visible.includes(columnID(k)));
  const from = rows.length === 0 ? 0 : offset + 1;
  const to = offset + rows.length;

  if (loading && rows.length === 0) {
    return (
      <div className={styles.card}>
        <div className={styles.tableLoading}>
          <Skeleton height={18} />
          <Skeleton height={18} />
          <Skeleton height={18} />
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return <div className={styles.card}>{empty}</div>;
  }

  return (
    <div className={styles.card}>
      <div className={styles.tableScroll}>
        <table
          className={compact ? styles.table : `${styles.table} ${styles.wide}`}
        >
          <thead>
            <tr>
              <th className={styles.stickyCol} scope="col">
                Name
              </th>
              {/* Second, the CSV's own place for it, and drawn in BOTH modes:
                  the avatar is a field of the row like the country beside it,
                  not a key of the card, so Compact keeps it and the column
                  picker never offers it. */}
              <th scope="col">Avatar</th>
              <th scope="col">Country</th>
              {/* Between Behaviour and the card in Columns mode, and between
                  Country and the folded card in Compact — the position it
                  holds in the row editor and in the CSV, so the three orders
                  are one order. */}
              {compact ? (
                <>
                  <th className={styles.num} scope="col">
                    Account age
                  </th>
                  <th scope="col">Card</th>
                </>
              ) : (
                <>
                  <th scope="col">Behaviour</th>
                  <th className={styles.num} scope="col">
                    Account age
                  </th>
                  {shown.map((k) => (
                    <th
                      key={columnID(k)}
                      scope="col"
                      className={isNumberType(k) ? styles.num : undefined}
                    >
                      {k.key}
                    </th>
                  ))}
                </>
              )}
              <th scope="col">Status</th>
              {/* The row menu's column. Its own button carries the name of
                  the row it belongs to, so a header here would only repeat
                  «Actions» once per row. */}
              <th className={styles.act} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className={row.status === "blocked" ? styles.bad : undefined}
              >
                {/* The mockup draws a 26px initials circle beside the name in
                    Columns mode and NONE in Compact — §2 against §3 — and the
                    difference is the point: Compact is the mode for reading a
                    wide schema end to end, and a glyph in the sticky column is
                    26px of the width that mode exists to give back. The
                    fallback needs no player: it is initials over a colour
                    derived from the name, exactly what the mockup inlines. */}
                <td className={styles.stickyCol}>
                  {compact ? (
                    <span className={styles.whoName}>{row.display_name}</span>
                  ) : (
                    <div className={styles.who}>
                      <Avatar name={row.display_name} size={26} />
                      <span className={styles.whoName}>{row.display_name}</span>
                    </div>
                  )}
                </td>
                <AvatarCell row={row} allowedAvatars={allowedAvatars} />
                <td className={styles.mono}>{row.country ?? "—"}</td>
                {compact ? (
                  <>
                    <td className={styles.num}>{ageCell(row.age_days)}</td>
                    <td className={styles.cardCell}>
                      {rowCard(row, cardKeys) || "—"}
                    </td>
                  </>
                ) : (
                  <>
                    <td>
                      {row.profile_key ? (
                        <Badge
                          variant={
                            row.profile_key === "leader" ? "accent" : "neutral"
                          }
                        >
                          {row.profile_key}
                        </Badge>
                      ) : (
                        <span className={styles.mono}>—</span>
                      )}
                    </td>
                    <td className={styles.num}>{ageCell(row.age_days)}</td>
                    {shown.map((k) => {
                      const bag = k.kind === "stat" ? row.stats : row.meta;
                      const has = carries(bag, k.key);
                      return (
                        <td
                          key={columnID(k)}
                          className={isNumberType(k) ? styles.num : styles.mono}
                        >
                          {has ? cellValue(bag[k.key]) : "—"}
                        </td>
                      );
                    })}
                  </>
                )}
                <td>
                  <StatusCell
                    row={row}
                    projectID={projectID}
                    onRename={() => onEdit(row)}
                  />
                </td>
                <td className={styles.act}>
                  <Menu
                    aria-label={`Actions for ${row.display_name}`}
                    items={rowMenu(row, onEdit, onDuplicate, onDelete)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={styles.cardFoot}>
        {/* A RANGE, not a count. «showing 50» over «200 rows» leaves an
            operator working out which fifty; «51–100» says it — and it is only
            allowed to say it because the buttons on the right can get there.
            The footer promised a range for months with no way past row 50. */}
        <span>
          {plural(total, "row", "rows")} · showing {from}–{to} · spent top to
          bottom
        </span>
        <span className={styles.footRight}>
          {/* One note per mode, each the one its own mockup gives.
              t-133 §1 replaces the Columns note with WHEN the avatar whitelist
              is applied — the rule of this table an operator cannot guess from
              the cells, since a marked row is spent like any other and the
              consequence lands on the bot instead. Compact keeps t-117 §3's
              sentence about the age, which t-133 does not draw and therefore
              does not overrule: the Account age column is still there, still
              printing a dash, and deleting the only line that explains it
              would be this task quietly undoing the last one. */}
          <span className={styles.mono}>
            {compact
              ? "An empty age is drawn, not zero — same rule as country and behaviour"
              : "bot_roster · avatar checked against profile.allowed_avatars when the row is spent"}
          </span>
          {/* Two buttons and not a numbered strip: the table is SPENT top to
              bottom, so «the next fifty» is the only movement through it that
              means anything — page 37 of a source of ten thousand names is a
              number nobody holds. Drawn only when there is a second page. */}
          {(offset > 0 || to < total) && (
            <span className={styles.pager}>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={offset === 0}
                onClick={() => onPage(Math.max(0, offset - PAGE_SIZE))}
              >
                ‹ Previous
              </button>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={to >= total}
                onClick={() => onPage(offset + PAGE_SIZE)}
              >
                Next ›
              </button>
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

/**
 * The row menu. A SPENT row is offered two items and not three: that row is a
 * bot now, the DELETE would come back `row_spent`, and an item that exists to
 * fail is worse than one that is not there. Editing one is still offered, and
 * is still harmless — the row is the recipe, not the player.
 */
function rowMenu(
  row: RosterRow,
  onEdit: (row: RosterRow) => void,
  onDuplicate: (row: RosterRow) => void,
  onDelete: (row: RosterRow) => void,
) {
  const items = [
    { label: "Edit row…", onSelect: () => onEdit(row) },
    { label: "Duplicate", onSelect: () => onDuplicate(row) },
  ];
  if (row.status === "used") return items;
  return [
    ...items,
    { separator: true as const },
    {
      label: "Delete row",
      variant: "danger" as const,
      onSelect: () => onDelete(row),
    },
  ];
}

/**
 * The Avatar cell: the asset id this row names, or a dash for «this row names
 * none» — the same dash, meaning the same thing, that Country and Behaviour
 * print.
 *
 * A value the project's whitelist does not list is drawn in the warn colour AND
 * carries the sentence as a `title`. The colour on its own is only a difference;
 * what an operator needs is the CONSEQUENCE, and it is not the one this table's
 * red rows carry — the row is not blocked, it will be spent, and the bot it
 * becomes simply arrives without an avatar.
 *
 * An empty whitelist marks nothing: the project declared no list, so there is
 * nothing for a value to be outside of. Nor is a SPENT row marked — its bot
 * exists, so the sentence has nothing left to warn about.
 */
function AvatarCell({
  row,
  allowedAvatars,
}: {
  row: RosterRow;
  allowedAvatars: string[];
}) {
  // `?? ""` and not `?? "—"`: a row that names no avatar is the empty case
  // whether the column arrived null or blank, and neither is «outside» a list
  // it was never measured against.
  const value = row.avatar ?? "";
  // A SPENT row is never marked. The warning is a forecast — «a bot created
  // from this row gets no avatar» — and for a row whose bot exists there is
  // nothing left to forecast and nothing to act on: that bot was created
  // already, wearing this or not, and the Pool tab is where it is looked at.
  // The contract puts the warning «before the row is spent» for this reason,
  // and the mockup marks an unused row.
  const outside =
    value !== "" &&
    row.status !== "used" &&
    allowedAvatars.length > 0 &&
    !allowedAvatars.includes(value);
  return (
    <td
      className={outside ? `${styles.mono} ${styles.avatarOutside}` : styles.mono}
      title={outside ? OUTSIDE_WHITELIST : undefined}
    >
      {value === "" ? "—" : value}
    </td>
  );
}

/** What the warn colour means, in words — see AvatarCell. */
const OUTSIDE_WHITELIST =
  "Outside the project's allowed-avatars whitelist — a bot created from this row gets no avatar";

function StatusCell({
  row,
  projectID,
  onRename,
}: {
  row: RosterRow;
  projectID: string;
  onRename: () => void;
}) {
  if (row.status === "used") {
    return (
      <span className={styles.statusCell}>
        <Badge variant="success" dot>
          used
        </Badge>
        {row.used_bot_id && (
          <Link
            className={styles.rowLink}
            to={`/projects/${projectID}/players/${row.used_bot_id}`}
          >
            {row.display_name} ›
          </Link>
        )}
      </span>
    );
  }
  if (row.status === "blocked") {
    return (
      <span className={styles.statusCell}>
        <Badge variant="danger" dot>
          {blockedLabel(row.blocked_reason)}
        </Badge>
        {/* The fix is in the dialog this opens: a living player carries the
            name, so the row has to give it up. */}
        <button type="button" className={styles.rowLink} onClick={onRename}>
          Rename ›
        </button>
      </span>
    );
  }
  return (
    <Badge variant="neutral" dot>
      unused
    </Badge>
  );
}

